import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clapperboard, History, MoveRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useOrdersList } from '@/api/legacy/orders';
import { rangeISO, useFilters } from '@/state/filters';
import {
  closeRiderOrders,
  useRiderOrdersDialogStore,
  type RiderOrdersDialogTarget,
} from '@/state/riderOrdersDialog';
import { fmt, TIMEZONE } from '@/lib/format';
import { statusLabel, statusTone } from '@/lib/status';
import { formatInTimeZone } from 'date-fns-tz';
import { cn } from '@/lib/cn';

type OrdersTab = 'ongoing' | 'completed' | 'all';

const ONGOING_STATUSES = 'created,packer_assigned,packed,billing,picked,reached';

function statusParamForTab(tab: OrdersTab): string {
  if (tab === 'ongoing') return ONGOING_STATUSES;
  if (tab === 'completed') return 'completed';
  return '';
}

/**
 * "Started → ended" cell for one delivery: picked timestamp (delivery
 * started) → completed timestamp (delivery ended) + ride duration.
 */
function DeliveryWindowCell({
  picked,
  completed,
}: {
  picked: string | null;
  completed: string | null;
}) {
  if (!picked) {
    return <span className="text-muted-foreground">Not picked yet</span>;
  }
  const startMs = new Date(picked).getTime();
  const endMs = completed ? new Date(completed).getTime() : NaN;
  const durationS =
    Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs
      ? (endMs - startMs) / 1000
      : null;
  return (
    <div className="min-w-[128px]">
      <div className="flex items-center gap-1 whitespace-nowrap">
        <span className="kpi-num text-foreground">{fmt.time(picked, 'HH:mm')}</span>
        <MoveRight className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
        <span className="kpi-num text-foreground">
          {completed ? fmt.time(completed, 'HH:mm') : 'en route'}
        </span>
        {durationS != null ? (
          <span className="kpi-num rounded-sm bg-muted px-1 py-px text-[10px] text-muted-foreground">
            {fmt.duration(durationS)}
          </span>
        ) : null}
      </div>
      <div className="mt-0.5 text-[10px] text-muted-foreground">
        {fmt.date(picked, 'd MMM, yyyy')}
      </div>
    </div>
  );
}

function RiderOrdersDialogBody({ target }: { target: RiderOrdersDialogTarget }) {
  const navigate = useNavigate();
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const globalStoreId = useFilters((s) => s.storeId);
  const [tab, setTab] = useState<OrdersTab>('ongoing');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const { start, end } = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);

  const storeId = target.storeId !== undefined ? target.storeId : globalStoreId;

  const status = statusParamForTab(tab);

  useEffect(() => {
    setPage(1);
  }, [tab, target.riderId, start, end, storeId]);

  const riderId = target.riderId;
  const query = useOrdersList({
    from: start,
    to: end,
    riderId,
    storeId: storeId ?? null,
    status,
    page,
    pageSize,
    sortBy: 'created_timestamp',
    sortOrder: 'desc',
  });

  const rows = query.data?.results ?? [];
  const total = query.data?.total_items ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const rangeLabel = useMemo(
    () =>
      `${formatInTimeZone(range.from, TIMEZONE, 'MMM d, yyyy')} – ${formatInTimeZone(range.to, TIMEZONE, 'MMM d, yyyy')}`,
    [range.from, range.to],
  );

  const titleName = target.riderName?.trim() || `Rider #${riderId}`;

  return (
    <>
      <div className="border-b border-border px-4 py-3 pr-12">
        <div className="flex items-start gap-2">
          <History className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-foreground">Delivery history</h2>
            <p className="truncate text-xs text-muted-foreground">{titleName}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {rangeLabel}
              {storeId != null ? ` · store #${storeId}` : ' · all stores in filter'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border px-4 py-2">
        {(
          [
            { key: 'ongoing' as const, label: 'Ongoing' },
            { key: 'completed' as const, label: 'Completed' },
            { key: 'all' as const, label: 'All' },
          ] as const
        ).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              'press rounded-full border px-2.5 py-1 text-[11px] font-medium focus-ring',
              tab === key
                ? 'border-primary/30 bg-primary/10 text-primary'
                : 'border-border bg-surface text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {query.isError ? (
          <div className="p-4">
            <ErrorState onRetry={() => void query.refetch()} />
          </div>
        ) : query.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No orders in this tab for the selected range.</p>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 z-[1] border-b border-border bg-surface-elevated">
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Order</th>
                  <th className="px-2 py-2 font-medium">Status</th>
                  <th className="px-2 py-2 font-medium">Delivery start → end</th>
                  <th className="hidden px-2 py-2 font-medium md:table-cell">Customer</th>
                  <th className="hidden px-2 py-2 font-medium lg:table-cell">Store</th>
                  <th className="px-2 py-2 text-right font-medium">Total</th>
                  <th className="px-4 py-2 text-right font-medium">
                    <span className="sr-only">Replay</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((o) => (
                  <tr key={o.id}>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        onClick={() => {
                          closeRiderOrders();
                          navigate(`/orders/${o.id}`);
                        }}
                        className="kpi-num rounded-sm font-mono text-primary hover:underline focus-ring"
                      >
                        #{o.id}
                      </button>
                    </td>
                    <td className="px-2 py-2">
                      <Badge tone={statusTone(o.status)} className="text-[10px]">
                        {statusLabel(o.status)}
                      </Badge>
                    </td>
                    <td className="px-2 py-2 text-[11px]">
                      <DeliveryWindowCell
                        picked={o.picked_timestamp}
                        completed={o.completed_timestamp}
                      />
                    </td>
                    <td className="hidden max-w-[120px] truncate px-2 py-2 text-muted-foreground md:table-cell">
                      {o.customer ?? '—'}
                    </td>
                    <td className="hidden max-w-[110px] truncate px-2 py-2 text-muted-foreground lg:table-cell">
                      {o.store_name ?? '—'}
                    </td>
                    <td className="kpi-num px-2 py-2 text-right tabular-nums text-foreground">
                      {fmt.npr(o.final_total)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {o.picked_timestamp ? (
                        <button
                          type="button"
                          title="Watch this delivery as a 3D replay"
                          aria-label={`Replay delivery of order ${o.id}`}
                          onClick={() => {
                            closeRiderOrders();
                            navigate(`/orders/${o.id}/replay`);
                          }}
                          className="press inline-flex h-6 items-center gap-1 rounded-full bg-rose-600 px-2 text-[10px] font-semibold text-white hover:bg-rose-700 focus-ring"
                        >
                          <Clapperboard className="h-3 w-3" /> Replay
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!query.isLoading && !query.isError && total > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-2">
            <span className="text-[11px] text-muted-foreground">
              {fmt.number(total)} orders
              {totalPages > 1 ? ` · page ${page} of ${totalPages}` : null}
            </span>
            {totalPages > 1 ? (
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
}

/**
 * Subscribes to {@link useRiderOrdersDialogStore} and mounts the modal.
 * Place once inside {@link AppShell} (needs router + filter context).
 */
export function RiderOrdersDialogRoot() {
  const open = useRiderOrdersDialogStore((s) => s.open);
  const target = useRiderOrdersDialogStore((s) => s.target);
  const close = useRiderOrdersDialogStore((s) => s.closeRiderOrders);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent
        className={cn(
          'flex max-h-[min(85vh,880px)] w-[calc(100vw-1.5rem)] max-w-3xl flex-col overflow-hidden p-0',
          'gap-0 sm:max-w-3xl',
        )}
      >
        <DialogTitle className="sr-only">Rider order history</DialogTitle>
        {target ? (
          <RiderOrdersDialogBody
            key={`${target.riderId}-${target.storeId === undefined ? 'global' : String(target.storeId)}`}
            target={target}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
